const KCAL_PER_KJ = 1 / 4.184;

const asFinite = (value) => {
  if (value == null || String(value).trim() === "") return null;
  const parsed = Number(typeof value === "string" ? value.trim().replace(",", ".") : value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function ageFromDateOfBirth(dateOfBirth, now = new Date()) {
  const match = String(dateOfBirth || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  let age = now.getFullYear() - year;
  if (now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day)) age -= 1;
  return age >= 13 && age <= 110 ? age : null;
}

function normalizeSex(value) {
  const sex = String(value || "").trim().toLowerCase();
  if (["male", "masculino", "m"].includes(sex)) return "male";
  if (["female", "feminino", "f"].includes(sex)) return "female";
  return null;
}

function averageHeartRate(samples, startedAt, stoppedAt) {
  const values = (Array.isArray(samples) ? samples : [])
    .filter((sample) => {
      const ts = asFinite(sample?.ts ?? sample?.time ?? sample?.timestamp);
      return (!startedAt || !ts || ts >= startedAt) && (!stoppedAt || !ts || ts <= stoppedAt);
    })
    .map((sample) => asFinite(sample?.bpm))
    .filter((bpm) => bpm != null && bpm >= 40 && bpm <= 230);
  if (values.length < 2) return null;
  return values.reduce((sum, bpm) => sum + bpm, 0) / values.length;
}

function activeKcalPerMinuteFromHeartRate({ bpm, weightKg, age, sex }) {
  const totalKjPerMinute = sex === "male"
    ? -55.0969 + 0.6309 * bpm + 0.1988 * weightKg + 0.2017 * age
    : -20.4022 + 0.4472 * bpm - 0.1263 * weightKg + 0.074 * age;
  const totalKcalPerMinute = Math.max(0, totalKjPerMinute * KCAL_PER_KJ);
  const restingKcalPerMinute = (3.5 * weightKg) / 200;
  return Math.max(0, totalKcalPerMinute - restingKcalPerMinute);
}

function activityMet({ activityMode, speedKmh, elevationGainM, distanceKm }) {
  const mode = String(activityMode || "").toLowerCase();
  const speed = Math.max(0, asFinite(speedKmh) || 0);
  if (mode.includes("bike") || mode.includes("cycl")) {
    if (speed >= 22) return 10;
    if (speed >= 16) return 8;
    if (speed >= 10) return 6.8;
    return 4;
  }
  const climbPerKm = distanceKm > 0 ? Math.max(0, elevationGainM) / distanceKm : 0;
  if (climbPerKm >= 75) return 7;
  if (climbPerKm >= 35) return 6;
  if (speed >= 6.4) return 5;
  if (speed >= 4.8) return 4.3;
  if (speed >= 3.2) return 3.5;
  return 2.8;
}

export function estimateActiveCalories({
  weightKg,
  dateOfBirth,
  sex,
  activityMode,
  movingMinutes,
  totalMinutes,
  speedKmh,
  elevationGainM = 0,
  distanceKm = 0,
  heartRateSamples = [],
  recordingStartedAt,
  recordingStoppedAt,
  now = new Date(),
} = {}) {
  const weight = asFinite(weightKg);
  const minutes = Math.max(0, asFinite(movingMinutes) || asFinite(totalMinutes) || 0);
  if (weight == null || weight < 30 || weight > 300 || minutes <= 0) return null;

  const age = ageFromDateOfBirth(dateOfBirth, now);
  const normalizedSex = normalizeSex(sex);
  const averageBpm = averageHeartRate(heartRateSamples, recordingStartedAt, recordingStoppedAt);
  let kcal;
  let method;
  if (averageBpm != null && age != null && normalizedSex) {
    kcal = activeKcalPerMinuteFromHeartRate({
      bpm: averageBpm,
      weightKg: weight,
      age,
      sex: normalizedSex,
    }) * minutes;
    method = "heart-rate";
  } else {
    const met = activityMet({ activityMode, speedKmh, elevationGainM, distanceKm });
    kcal = Math.max(0, met - 1) * 3.5 * weight / 200 * minutes;
    method = "activity";
  }
  if (!Number.isFinite(kcal)) return null;
  return { activeKcal: Math.round(kcal), method };
}

export function estimateCaloriesForHistoryEntry(entry, profile) {
  const stored = asFinite(entry?.stats?.caloriesKcal ?? entry?.caloriesKcal);
  if (stored != null && stored >= 0) return { activeKcal: Math.round(stored), method: "stored" };
  const stats = entry?.stats || {};
  return estimateActiveCalories({
    weightKg: profile?.weightKg ?? (
      typeof localStorage !== "undefined"
        ? localStorage.getItem("trekkin_health_weight_kg")
        : null
    ),
    dateOfBirth: profile?.dob,
    sex: profile?.sex || profile?.gender,
    activityMode: entry?.activityMode,
    movingMinutes: stats.movingMin,
    totalMinutes: stats.totalMin,
    speedKmh: stats.speed,
    elevationGainM: stats.dPlus,
    distanceKm: stats.distKm,
    heartRateSamples: entry?.heartRate?.samples,
    recordingStartedAt: entry?.recordingStartedAt,
    recordingStoppedAt: entry?.recordingStoppedAt,
  });
}

export function withEstimatedHistoryCalories(entry, profile) {
  const estimate = estimateCaloriesForHistoryEntry(entry, profile);
  if (!estimate || !entry) return entry;
  return {
    ...entry,
    caloriesKcal: estimate.activeKcal,
    stats: { ...(entry.stats || {}), caloriesKcal: estimate.activeKcal },
  };
}
